import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './breadcrumb-item.scss?inline';
import '@principal/design-system-icons-web/chevron-right';
import '@principal/design-system-icons-web/chevron-left';
import { requiredSlot } from '../../decorators/requiredSlot';

/**
 * @summary A breadcrumb-item (li) element
 *
 * @slot default Required: The content of the breadcrumb item
 *
 * @fires pds-breadcrumb-item-click A custom event dispatched on click
 */

@customElement('pds-breadcrumb-item', {
  category: 'component',
  type: 'component',
  state: 'stable',
  styles,
})
export class PdsBreadcrumbItem extends PdsElement {
  /**
   * - Active denotes current breadcrumb-item rendering it non-clickable.
   */
  @property({ type: Boolean })
  active = false;

  /**
   * - Href supplies the url for the breadcrumb link
   */
  @property({ type: String })
  href = '#';

  /**
   * @internal
   */
  handleClick(e: MouseEvent) {
    const customEvent = new CustomEvent('pds-breadcrumb-item-click', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        summary: this.textContent,
      },
    });

    this.dispatchEvent(customEvent);

    if (customEvent.defaultPrevented) {
      e.preventDefault();
    }
  }

  /**
   * @internal
   */
  getBreadcrumb(): unknown {
    if (this.active) {
      return html`<span
        part="breadcrumb-link"
        class="${this.classEl('text')}"
        aria-current="page"
        ><slot>Breadcrumb</slot></span
      >`;
    }

    return html`<pds-icon-chevron-left size="xs"></pds-icon-chevron-left
      ><a
        @click=${this.handleClick}
        part="breadcrumb-link"
        class="${this.classEl('link')}"
        href="${this.href}"
        ><slot>Breadcrumb</slot></a
      ><pds-icon-chevron-right size="xs"></pds-icon-chevron-right>`;
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      active: this.active,
    };
  }

  @requiredSlot(['default'])
  render() {
    return html`<li class=${this.getClass()} role="listitem">
      ${this.getBreadcrumb()}
    </li>`;
  }
}
